import React, { useId } from "react";
import { ArrowRight } from "lucide-react";
import styles from "./PremiersPas.module.css";

/**
 * Premier écran après une inscription : un titre, une ligne, trois choix (ou plus) pour la
 * première action de valeur, puis le panneau du choix ouvert. Une seule action à la fois ;
 * le bloc remplace l'en-tête de la page et disparaît quand l'action est faite.
 */
export type ChoixPremiersPas = { id: string; label: string; detail?: string };

export type PremiersPasProps = {
  titre: string;
  texte: string;
  choix: ChoixPremiersPas[];
  actif: string | null;
  onChoix: (id: string) => void;
  /** Panneau du choix ouvert (saisie, aperçu, confirmation). */
  children?: React.ReactNode;
  /** Ligne en petit sous le bloc (durée d'essai, absence de carte bancaire). */
  pied?: React.ReactNode;
  occupe?: boolean;
};

export function PremiersPas({ titre, texte, choix, actif, onChoix, children, pied, occupe = false }: PremiersPasProps) {
  const id = useId();
  return (
    <section className={styles.bloc} aria-labelledby={`${id}-t`}>
      <h1 className={styles.titre} id={`${id}-t`}>{titre}</h1>
      <p className={styles.texte}>{texte}</p>
      {choix.length > 0 && <div className={styles.choix}>
        {choix.map((c) => (
          <button
            key={c.id}
            type="button"
            className={styles.carte}
            aria-pressed={actif === c.id}
            disabled={occupe}
            onClick={() => onChoix(c.id)}
          >
            <strong>{c.label}</strong>
            {c.detail && <span>{c.detail}</span>}
            {/* Repère visible sans survol (téléphone) : chaque choix est une action. */}
            <ArrowRight aria-hidden="true" className={styles.fleche} />
          </button>
        ))}
      </div>}
      {/* Les conditions du parent donnent [false, null…] : un tableau toujours vrai, d'où toArray. */}
      {React.Children.toArray(children).length > 0 && <div className={styles.panneau}>{children}</div>}
      {pied && <p className={styles.pied}>{pied}</p>}
    </section>
  );
}
